// =============================================================
// shareLink.ts — create + redeem tracked share/referral links.
//
// Patrick rule 2026-04-26: every Share/Copy/QR tap on a venue creates
// a unique tracked row in referrals.share_links so we can attribute
// opens + conversions back to the sharer (gamification later).
// =============================================================

const API_BASE = ((import.meta as any).env?.VITE_API_URL || 'https://core.honestlee.app').replace(/\/$/, '');

export type ShareMethod = 'copy' | 'qr' | 'native_share';

export interface ShareLinkResponse {
  success: boolean;
  token: string;
  url: string;
  venue_id: number;
  slug: string;
}

/** Best-effort GPS — returns null silently on permission denied / unavailable. */
function getCurrentCoords(timeoutMs = 2500): Promise<{ lat: number; lng: number } | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    let done = false;
    const t = setTimeout(() => { if (!done) { done = true; resolve(null); } }, timeoutMs);
    navigator.geolocation.getCurrentPosition(
      (pos) => { if (!done) { done = true; clearTimeout(t); resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }); } },
      () => { if (!done) { done = true; clearTimeout(t); resolve(null); } },
      { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 60000 },
    );
  });
}

/**
 * Create a tracked share link for a venue.
 * Returns the URL with embedded ?ref=<token> ready to be shared/copied/QR'd.
 *
 * @param venueId      — venues.venues.id (number)
 * @param shareMethod  — 'copy' | 'qr' | 'native_share' (which UI button was tapped)
 * @param shareSource  — string label for analytics: 'consumer_drawer', 'consumer_at_page', etc.
 */
export async function createShareLink(
  venueId: number,
  shareMethod: ShareMethod,
  shareSource: string = 'consumer_drawer',
): Promise<ShareLinkResponse | null> {
  try {
    const coords = await getCurrentCoords();
    const token = localStorage.getItem('venue_app_token') || '';

    const r = await fetch(`${API_BASE}/api/shares`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        venue_id: venueId,
        share_method: shareMethod,
        share_source: shareSource,
        lat: coords?.lat ?? null,
        lng: coords?.lng ?? null,
      }),
    });
    if (!r.ok) return null;
    return (await r.json()) as ShareLinkResponse;
  } catch (e) {
    console.warn('[createShareLink] failed:', e);
    return null;
  }
}

// v68.236 — event-flavoured share. Posts { event_id, route_key:'event' }
// and returns the OG-prefixed URL: https://core.honestlee.app/og/event/<slug>?ref=<token>.
export interface EventShareLinkResponse {
  success: boolean;
  token: string;
  url: string;
  event_id: string;
  slug: string;
  route_key: string;
}

export async function createEventShareLink(
  eventId: string,
  shareMethod: ShareMethod,
  shareSource: string = 'consumer_event_detail',
): Promise<EventShareLinkResponse | null> {
  try {
    const coords = await getCurrentCoords();
    const token = localStorage.getItem('hl_auth_token') || localStorage.getItem('venue_app_token') || '';
    const r = await fetch(`${API_BASE}/api/shares`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        event_id: eventId,
        route_key: 'event',
        share_method: shareMethod,
        share_source: shareSource,
        lat: coords?.lat ?? null,
        lng: coords?.lng ?? null,
      }),
    });
    if (!r.ok) return null;
    return (await r.json()) as EventShareLinkResponse;
  } catch (e) {
    console.warn('[createEventShareLink] failed:', e);
    return null;
  }
}

// EV-833 — share the event's PHOTO GALLERY. Same as the event share but route_key
// 'event_gallery' → backend returns https://core.honestlee.app/og/gallery/<slug>?ref=…
// whose OG preview is the photo collage (so a re-share visibly shows the photos).
export async function createEventGalleryShareLink(
  eventId: string,
  shareMethod: ShareMethod,
  shareSource: string = 'consumer_event_gallery',
): Promise<EventShareLinkResponse | null> {
  try {
    const coords = await getCurrentCoords();
    const token = localStorage.getItem('hl_auth_token') || localStorage.getItem('venue_app_token') || '';
    const r = await fetch(`${API_BASE}/api/shares`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({
        event_id: eventId,
        route_key: 'event_gallery',
        share_method: shareMethod,
        share_source: shareSource,
        lat: coords?.lat ?? null,
        lng: coords?.lng ?? null,
      }),
    });
    if (!r.ok) return null;
    return (await r.json()) as EventShareLinkResponse;
  } catch (e) {
    console.warn('[createEventGalleryShareLink] failed:', e);
    return null;
  }
}

// v68.236 — global feed/map share (no entity id).
// CON-1503 — 'gallery_feed' joins them: the City Gallery share now mints a tracked,
// attributed link too. `extra` carries the city + active filters so the OG card
// describes the exact view that was shared and counts the right city.
export async function createGlobalShareLink(
  routeKey: 'events_feed' | 'events_map' | 'gallery_feed',
  shareMethod: ShareMethod = 'native_share',
  shareSource?: string,
  extra?: Record<string, string>,
): Promise<{ success: boolean; token: string; url: string; route_key: string } | null> {
  try {
    const coords = await getCurrentCoords();
    const token = localStorage.getItem('hl_auth_token') || localStorage.getItem('venue_app_token') || '';
    const r = await fetch(`${API_BASE}/api/shares`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        route_key: routeKey,
        share_method: shareMethod,
        share_source: shareSource || routeKey,
        lat: coords?.lat ?? null,
        lng: coords?.lng ?? null,
        ...(extra || {}),
      }),
    });
    if (!r.ok) return null;
    return await r.json();
  } catch (e) {
    console.warn('[createGlobalShareLink] failed:', e);
    return null;
  }
}

// =============================================================
// EV-470 — fan a tracked share URL out to a specific social platform.
// The URL must already be the OG-prefixed, ?ref=<token> tracked link from one of
// the create*ShareLink helpers above so opens/conversions stay attributable.
// =============================================================
export type SharePlatform = 'telegram' | 'x' | 'facebook' | 'whatsapp' | 'instagram' | 'copy' | 'native';

/** Build the platform's web share-intent URL for a tracked link, or null if the
 *  platform has no URL-based share intent (Instagram, copy, native). */
export function socialShareIntentUrl(platform: SharePlatform, url: string, text: string): string | null {
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(text);
  switch (platform) {
    case 'telegram': return `https://t.me/share/url?url=${u}&text=${t}`;
    case 'x':        return `https://twitter.com/intent/tweet?url=${u}&text=${t}`;
    case 'facebook': return `https://www.facebook.com/sharer/sharer.php?u=${u}`;
    case 'whatsapp': return `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`;
    default:         return null;   // instagram / copy / native handled by the caller
  }
}

/** Open the share intent for a platform. For Instagram/copy it copies the link to
 *  the clipboard (IG has no web share-by-URL); for native it uses navigator.share.
 *  Returns 'opened' | 'copied' | 'shared' | 'failed'. */
export async function shareToPlatform(
  platform: SharePlatform, url: string, text: string,
): Promise<'opened' | 'copied' | 'shared' | 'failed'> {
  try {
    if (platform === 'native' && (navigator as any).share) {
      await (navigator as any).share({ title: 'HonestLee', text, url });
      return 'shared';
    }
    if (platform === 'instagram' || platform === 'copy') {
      await navigator.clipboard.writeText(url);
      return 'copied';
    }
    const intent = socialShareIntentUrl(platform, url, text);
    if (intent) { window.open(intent, '_blank', 'noopener,noreferrer'); return 'opened'; }
    // fallback: copy
    await navigator.clipboard.writeText(url);
    return 'copied';
  } catch {
    return 'failed';
  }
}

/**
 * Log an open. Called when a recipient lands on a URL with ?ref=<token>.
 * Idempotent server-side — safe to call multiple times.
 */
export async function logShareOpen(token: string): Promise<void> {
  try {
    await fetch(`${API_BASE}/api/shares/${encodeURIComponent(token)}`, { method: 'GET' });
  } catch { /* best-effort */ }
}

/**
 * Stash incoming ?ref=<token> in sessionStorage so we can later attribute
 * a sign-up event back to the original sharer.
 */
export function captureRefIfPresent(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      sessionStorage.setItem('hl_ref_token', ref);
      // Best-effort log the open — fire and forget
      logShareOpen(ref);
      return ref;
    }
  } catch { /* SSR / sandboxed iframe */ }
  return null;
}

/**
 * Mark a stashed ref token as converted (call this on signup completion).
 */
export async function markRefConverted(userId: string, event: string = 'signup'): Promise<void> {
  try {
    const ref = sessionStorage.getItem('hl_ref_token');
    if (!ref) return;
    await fetch(`${API_BASE}/api/shares/${encodeURIComponent(ref)}/convert`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, conversion_event: event }),
    });
    sessionStorage.removeItem('hl_ref_token');
  } catch { /* best-effort */ }
}
